/**
 * E4 five phases (data/phases.ts): rules on skilled actions, no flat duel damage.
 * - observe `hit`: a break / shatter / counter by an overcoming art keeps the foe open +0.2 s (`crack` events for the
 *   client to tint); a player hit on a monster of the overcome phase adds x1.15 (never vs players); an armed generating
 *   switch adds +10 % to the next hit.
 * - step: polls the player's active art; hit then switch (within 3 s) to the generated phase arms the bonus, once per 15 s.
 * - observe `skill`: the armed move refunds half its qi.
 * All behind FIVE_PHASES_ON. Time only from World.time; no randomness.
 */
import type { SystemHost, WorldSystem } from './types';
import type { WorldEvent } from '../world';
import { SKILLS } from '../data/skills';
import { FIVE_PHASES_ON, PHASE_RULES, artPhase, generates, monsterPhase, overcomes, type Phase } from '../data/phases';

export class PhasesSystem implements WorldSystem {
  readonly name = 'phases';
  private lastArt = '';
  private lastHitT = -99;
  /** The phase of the last move each fighter used (the foe's phase for overcoming). */
  private readonly lastPhase = new Map<number, Phase>();
  private nextGenT = 0;
  /** Phase of the art whose switch armed the bonus (or null). */
  armedPhase: Phase | null = null;
  /** Open-time extensions given (for the client crack FX): the latest. */
  lastCrack: { tgtId: number; phase: Phase; t: number } | null = null;
  constructor(private readonly h: SystemHost) {}

  private skillArt(skillId: string): Phase | undefined {
    return artPhase(artOfSkill(skillId) ?? '');
  }

  step(): void {
    if (!FIVE_PHASES_ON) return;
    const w = this.h.world;
    const cur = w.progress.activeArt;
    if (!this.lastArt) this.lastArt = cur;
    if (cur === this.lastArt) return;
    const from = artPhase(this.lastArt);
    const to = artPhase(cur);
    this.lastArt = cur;
    if (w.time - this.lastHitT <= PHASE_RULES.switchWindow && w.time >= this.nextGenT && generates(from, to)) {
      this.armedPhase = to ?? null;
      this.nextGenT = w.time + PHASE_RULES.genCooldown;
    }
  }

  observe(e: WorldEvent): void {
    if (!FIVE_PHASES_ON) return;
    const w = this.h.world;
    const me = w.player;
    if (e.type === 'skill') {
      const ph = this.skillArt(e.skillId);
      if (ph) this.lastPhase.set(e.srcId, ph);
      if (e.srcId === me.id && this.armedPhase && ph === this.armedPhase) {
        const s = SKILLS[e.skillId];
        if (s) me.qi = Math.min(me.maxQi, me.qi + s.qi * (1 - PHASE_RULES.genQiMul));
      }
      return;
    }
    if (e.type !== 'hit') return;
    const src = w.get(e.srcId);
    const tgt = w.get(e.tgtId);
    if (!src || !tgt) return;
    const mine = src === me;
    const shp = this.skillArt(e.skillId);
    if (shp) this.lastPhase.set(src.id, shp);
    if (mine) this.lastHitT = w.time;
    const sp = shp;
    const open = e.outcome === 'guardBreak' || e.outcome === 'guardShatter' || e.outcome === 'countered';
    // Overcoming: the foe stays open longer (players only; the attacker's phase beats the foe's active art).
    if (open && tgt.kind !== 'monster') {
      const ap = shp;
      const fp = tgt === me ? artPhase(w.progress.activeArt) : this.lastPhase.get(tgt.id);
      if (overcomes(ap, fp) && ap) {
        tgt.stunT += PHASE_RULES.openExtra;
        this.lastCrack = { tgtId: tgt.id, phase: ap, t: w.time };
      }
    }
    if (!mine || e.damage <= 0 || e.outcome === 'blocked' || tgt.dead) return;
    let mul = 1;
    if (tgt.kind === 'monster' && overcomes(sp ?? artPhase(w.progress.activeArt), monsterPhase(tgt.brain?.defId ?? ''))) mul *= PHASE_RULES.pveMul;
    if (this.armedPhase) {
      mul *= PHASE_RULES.genDamage;
      this.armedPhase = null;
    }
    if (mul !== 1 && !tgt.dead) tgt.hp = Math.max(1, tgt.hp - Math.round(e.damage * (mul - 1)));
  }
}

import { MARTIAL_ARTS } from '../data/skills';
const SKILL_ART = new Map<string, string>();
function artOfSkill(skillId: string): string | undefined {
  if (!SKILL_ART.size) for (const a of Object.values(MARTIAL_ARTS)) for (const s of a.skills) SKILL_ART.set(s, a.id);
  return SKILL_ART.get(skillId);
}
